import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useIsDesktop } from '../../hooks';
import styles from './ProfileMenu.module.scss';
import { UserIcon, ArrowRightOnRectangleIcon, Cog6ToothIcon, HeartIcon } from '@heroicons/react/24/outline';

const ProfileMenu: React.FC = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const isDesktop = useIsDesktop();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleLogout = async () => {
    await logout();
    setIsOpen(false);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Hide profile menu on tablet/mobile screens
  if (!isDesktop) {
    return null;
  }

  // Show login/register buttons if not authenticated
  if (!isAuthenticated) {
    return (
      <div className={styles.authButtons}>
        <Link to="/login" className={styles.loginButton}>
          Masuk
        </Link>
        <Link to="/register" className={styles.registerButton}>
          Daftar
        </Link>
      </div>
    );
  }

  // Show profile menu if authenticated
  return (
    <div className={styles.profileMenuContainer} ref={menuRef}>
      <button
        className={styles.profileButton}
        onClick={toggleMenu}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {user?.profilePictureUrl ? (
          <img
            src={user.profilePictureUrl}
            alt={`${user.name}'s profile`}
            className={styles.profileImage}
          />
        ) : (
          <div className={styles.profileInitial}>
            {user?.name?.charAt(0) || 'U'}
          </div>
        )}
        <span className={styles.profileButtonText}>{user?.name || 'User'}</span>
      </button>

      {isOpen && (
        <div className={styles.menuDropdown}>
          <div className={styles.userInfo}>
            <h3 className={styles.userName}>{user?.name || 'User'}</h3>
            <p className={styles.userEmail}>{user?.email || ''}</p>
          </div>

          <div className={styles.menuItems}>
            <Link to="/profile" className={styles.menuItem} onClick={() => setIsOpen(false)}>
              <UserIcon className={styles.menuIcon} />
              <span>Profil Saya</span>
            </Link>
            <Link to="/my-donation-history" className={styles.menuItem} onClick={() => setIsOpen(false)}>
              <HeartIcon className={styles.menuIcon} />
              <span>Riwayat Donasi</span>
            </Link>
            <Link to="/settings" className={styles.menuItem} onClick={() => setIsOpen(false)}>
              <Cog6ToothIcon className={styles.menuIcon} />
              <span>Pengaturan</span>
            </Link>
            <button className={styles.menuItem} onClick={handleLogout}>
              <ArrowRightOnRectangleIcon className={styles.menuIcon} />
              <span>Keluar</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileMenu;
